import { AlertTriangle, Check, FileDiff, Loader, XCircle } from "lucide-react";
import type { ChangeSetInfo } from "@racler/protocol";
import { changeLabel, changeLetter, statusText } from "../lib/review.ts";

const MAX_LISTED = 8;

/** A proposal inside the conversation: status line, M/A/D/R summary and the Review button. */
export function ChangeSetCard({ cs, onReview }: { cs: ChangeSetInfo; onReview: (id: string) => void }) {
  const Icon =
    cs.status === "applying" || cs.status === "approved"
      ? Loader
      : cs.status === "applied"
        ? Check
        : cs.status === "conflicted" || cs.status === "failed"
          ? AlertTriangle
          : cs.status === "rejected" || cs.status === "discarded"
            ? XCircle
            : FileDiff;
  const reviewable = cs.status !== "discarded";
  return (
    <div
      className={`changeset cs-${cs.status}`}
      data-testid="changeset"
      data-status={cs.status}
      data-id={cs.id}
    >
      <div className="changeset-head">
        <Icon size={14} aria-hidden className={cs.status === "applying" ? "spin" : ""} />
        <span data-testid="changeset-status">{statusText(cs)}</span>
      </div>
      {cs.files.length > 0 && (
        <ul className="changeset-files">
          {cs.files.slice(0, MAX_LISTED).map((f) => (
            <li key={`${f.op}:${f.path}`} title={changeLabel(f)}>
              <span className={`chg chg-${changeLetter(f.op)}`}>{changeLetter(f.op)}</span>
              <code>{f.oldPath ? `${f.oldPath} → ${f.path}` : f.path}</code>
              <span className="muted small">
                {" "}
                +{f.additions} −{f.deletions}
              </span>
            </li>
          ))}
          {cs.files.length > MAX_LISTED && (
            <li className="muted small">…and {cs.files.length - MAX_LISTED} more</li>
          )}
        </ul>
      )}
      {cs.message && (cs.status === "conflicted" || cs.status === "failed") && (
        <div className="muted small">{cs.message}</div>
      )}
      {reviewable && (
        <button onClick={() => onReview(cs.id)} data-testid="review-open">
          {cs.status === "pending_approval" ? "Review changes" : "View changes"}
        </button>
      )}
    </div>
  );
}
